/** A module an agent can grant (or withhold from) a Sales Assistant acting as them. */
export interface AssistantAccessModule {
  key: string;
  label: string;
  /** PrimeIcons class shown on the module tile. */
  icon: string;
  hint: string;
  /** Permission keys that belong to this module — stripped from the session while acting-as when the module isn't granted. */
  permissions: string[];
  /** Permission key prefixes that belong to this module, for families too large/dynamic to list key by key. */
  permissionPrefixes?: string[];
  /** Sidebar link prefixes that belong to this module — hidden from the menu while acting-as when the module isn't granted. */
  links: string[];
  /** Always granted — act-as lands on the dashboard, so it can never be withheld. */
  isLocked?: boolean;
  /** Not offered on the Access tab and never granted, even if an older grant still lists it. Remove the flag to offer the module. */
  isHidden?: boolean;
}

/**
 * Mirrors the sidebar groups in `side-menu.model.ts`. The keys are what the backend stores in
 * `allowed_modules`, so don't rename one without migrating existing grants
 * (see `docs/backend/sales-assistant-agent-approval.md`).
 */
export const ASSISTANT_ACCESS_MODULES: AssistantAccessModule[] = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    icon: 'pi pi-th-large',
    hint: 'Always visible — act-as opens here',
    permissions: [],
    links: ['/dashboard'],
    isLocked: true,
  },
  // Hidden until Email is released to clients.
  {
    key: 'email',
    label: 'Email',
    icon: 'pi pi-envelope',
    hint: 'Inbox and lead emails',
    permissions: ['leademail.manage'],
    links: ['/my-inbox'],
    isHidden: true,
  },
  {
    key: 'calendar',
    label: 'Calendar',
    icon: 'pi pi-calendar',
    hint: 'Appointments and events',
    permissions: ['calendar.manage'],
    links: ['/calender', '/my-calander'],
  },
  { key: 'tasks', label: 'Tasks', icon: 'pi pi-check-square', hint: 'Task lists and follow-ups', permissions: ['task.show'], links: ['/tasks'] },
  {
    key: 'leads',
    label: 'Leads',
    icon: 'pi pi-user-plus',
    hint: 'Lead pipeline and details',
    permissions: ['lead.manage', 'lead.view'],
    links: ['/leads'],
  },
  {
    key: 'contacts',
    label: 'Contacts',
    icon: 'pi pi-users',
    hint: 'Contacts, staff and teams',
    permissions: ['contact.view', 'developer.view', 'user.view', 'staff.view', 'team.manage', 'offices.manage', 'groups.manage'],
    links: ['/setting/office-contact'],
  },
  {
    key: 'listings',
    label: 'Listings',
    icon: 'pi pi-home',
    hint: 'Property listings',
    permissions: ['allproperties.manage', 'allproperties.view'],
    links: ['/listings/listing-properties'],
  },
  {
    key: 'projects',
    label: 'Projects',
    icon: 'pi pi-building',
    hint: 'Projects and stock',
    permissions: ['allprojects.edit', 'allprojects.view', 'allprojects.manage'],
    links: ['/project'],
  },
  {
    key: 'contracts',
    label: 'Contracts',
    icon: 'pi pi-file-edit',
    hint: 'Contracts and key dates',
    permissions: ['contract.manage', 'contract.view'],
    links: ['/contracts'],
  },
  {
    key: 'reports',
    label: 'Reports',
    icon: 'pi pi-chart-bar',
    hint: 'Reports and analytics',
    permissions: ['report.manage', 'report.view', 'report.create', 'report.edit', 'report.delete'],
    links: ['/report'],
  },
  { key: 'history', label: 'History', icon: 'pi pi-history', hint: 'Activity history', permissions: ['history.view'], links: ['/history'] },
  {
    key: 'library',
    label: 'Library',
    icon: 'pi pi-folder-open',
    hint: 'Documents and files',
    permissions: ['library.icentra.view', 'library.office.view', 'library.settings.manage', 'setting.library.setup'],
    links: ['/library'],
  },
  {
    key: 'market_place',
    // Hidden until Market Place goes live.
    isHidden: true,
    label: 'Market Place',
    icon: 'pi pi-shopping-bag',
    hint: 'Market place listings',
    permissions: ['market.view'],
    links: ['/listings/market-place'],
  },
  // Chat has no permission key at all, so it can only be withheld through the sidebar link filter.
  { key: 'chat', label: 'Chat', icon: 'pi pi-comments', hint: 'Internal team chat', permissions: [], links: ['/chat'] },
  {
    key: 'settings',
    // Never grantable — an assistant must not reach office settings, so this stays hidden and always blocked.
    isHidden: true,
    label: 'Settings',
    icon: 'pi pi-cog',
    hint: 'Office setup and configuration',
    permissions: [
      'entitymanager.manage',
      'task.jobtype.manage',
      'task.priority.manage',
      'task.marketing.manage',
      'task.status.create',
      'task.status.edit',
      'task.status.delete',
      'permissions.manage',
      'level.manage',
      'menu.add',
      'menu.edit',
      'rolemenu.add',
      'rolemenu.edit',
    ],
    // The sidebar gates Settings on a synthetic 'setting.office.setup' plus a family of 'setting.*' keys, so match by prefix.
    permissionPrefixes: ['setting.'],
    // '/setting/office-contact' is longer, so Contacts still owns that link.
    links: ['/setting'],
  },
];

const isGranted = (module: AssistantAccessModule, allowedKeys: string[]): boolean =>
  !module.isHidden && (module.isLocked || allowedKeys.includes(module.key));

/** Drops the permission keys of every module the agent hasn't granted. Permissions outside the listed modules pass through untouched. */
export function restrictPermissionsToModules(permissions: string[], allowedKeys: string[]): string[] {
  const blockedModules = ASSISTANT_ACCESS_MODULES.filter(module => !isGranted(module, allowedKeys));
  const blocked = new Set(blockedModules.flatMap(module => module.permissions));
  const blockedPrefixes = blockedModules.flatMap(module => module.permissionPrefixes || []);
  return (permissions || []).filter(permission => !blocked.has(permission) && !blockedPrefixes.some(prefix => permission?.startsWith(prefix)));
}

/** False when the menu link belongs to a module the agent hasn't granted. */
export function isMenuLinkAllowed(link: string, allowedKeys: string[]): boolean {
  if (!link) {
    return true;
  }
  let owner: AssistantAccessModule = null;
  let ownerPrefixLength = 0;
  ASSISTANT_ACCESS_MODULES.forEach(module => {
    module.links.forEach(prefix => {
      if (link.startsWith(prefix) && prefix.length > ownerPrefixLength) {
        owner = module;
        ownerPrefixLength = prefix.length;
      }
    });
  });
  return !owner || isGranted(owner, allowedKeys);
}
